import { AddCircleOutline, Delete, Edit } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import { DropzoneArea } from "react-mui-dropzone";
import { useParams } from "react-router-dom";
import { SnackbarContext } from "../../contexts/Snackbar";
import { CRIMSON_RED, HOT_PINK } from "../../enums/Colors";
import {
  isHeicFile,
  maxImageUploadSize,
  supportedImageTypes,
} from "../../enums/Consts";
import { CHIP_MIN_WIDTH } from "../../enums/Styles";
import { PET_AVATAR_DELETE, PET_AVATAR_UPLOAD } from "../../enums/ApiPaths";
import { PET_AVATAR } from "../../enums/QueryKeys";
import useFormDelete from "../../hooks/useFormDelete";
import usePrimaryColor from "../../hooks/usePrimaryColor";
import { snackbarSeverity } from "../../hooks/useSnackbar";
import api from "../../utils/api/authApi";
import { trimText, useIsMobile } from "../../utils/Functions";
import {
  getGenderAvatarTextColor,
  getUnknownGenderColor,
  isUnknownGender,
} from "../../features/pets/utils/Functions";
import { getFirstLetter } from "./EntityCard";
const avatarSize = 150;

export default function AvatarDisplay({
  img,
  name,
  gender,
  deceased = false,
  size,
  addAvatar = false,
  loadingAvatar,
  icon = null,
  testId,
}) {
  const color = usePrimaryColor();
  const { mode } = useTheme().palette;
  const { t } = useTranslation();
  const { id } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);

  const deleting = useFormDelete(
    PET_AVATAR_DELETE,
    id,
    "DeletePetAvatarConfirmationMessage",
  );
  const avatarColorCoding = (gender) =>
    Boolean(deceased)
      ? "gray"
      : isUnknownGender(gender)
        ? getUnknownGenderColor(mode)
        : String(gender).toUpperCase() === "MALE"
          ? color
          : HOT_PINK;
  const letterColor = getGenderAvatarTextColor(gender, mode, deceased);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleAddClick = () => {
    handleClosePopover();
    setOpenDialog(true);
  };

  const handleRemoveClick = async () => {
    handleClosePopover();
    const confirm = await deleting.submitForm();
    if (confirm) {
      setSelectedFile(null);
      queryClient.invalidateQueries({ queryKey: [PET_AVATAR, id] });
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedFile(null);
  };

  const handleSelectedFile = (file) => {
    if (file) {
      setSelectedFile(file);
    }
  };
  const handleSave = async () => {
    if (!selectedFile) {
      handleSnackbar("SelectImage", snackbarSeverity.ERROR);
      return;
    }

    const formData = new FormData();
    formData.append("image", selectedFile);

    try {
      setLoading(true);
      const path = `${PET_AVATAR_UPLOAD}${id}`;
      const response = await api.post(path, formData);

      if (response.status === 200) {
        queryClient.invalidateQueries({ queryKey: [PET_AVATAR, id] });

        handleSnackbar("", snackbarSeverity.SUCCESS);
        setSelectedFile(null);
      }
    } catch (e) {
      handleSnackbar(e?.response?.data, snackbarSeverity.ERROR);
    } finally {
      setLoading(false);
      handleDialogClose();
    }
  };

  if (loading || loadingAvatar) {
    return (
      <Box
        sx={{
          position: "relative",
          width: size || avatarSize,
          height: size || avatarSize,
          mx: "auto",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3,
            borderRadius: "50%",
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: size || avatarSize,
        height: size || avatarSize,
        mx: "auto",
      }}
    >
      <Avatar
        src={img || undefined}
        data-testid={testId}
        sx={{
          width: "100%",
          height: "100%",
          bgcolor: gender && !img ? avatarColorCoding(gender) : color,
          color: letterColor,
        }}
        slotProps={{
          img: {
            sx: {
              filter: deceased
                ? "grayscale(100%) brightness(0.8) contrast(0.95)"
                : "none",
            },
          },
        }}
      >
        {!icon ? (
          <Typography variant="h1" fontWeight="bold">
            {String(getFirstLetter(name)).toUpperCase()}
          </Typography>
        ) : (
          <i
            className={`fas ${icon}`}
            style={{
              fontSize: 80,
              color: letterColor,
            }}
          />
        )}
      </Avatar>

      {addAvatar && (
        <IconButton
          onClick={handleMenuClick}
          title={t("Edit")}
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            color: "white",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.8)",
            },
            zIndex: 2,
          }}
        >
          <Edit fontSize="small" />
        </IconButton>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <MenuItem onClick={handleAddClick} sx={{ p: 2 }}>
          <AddCircleOutline sx={{ mr: 1 }} fontSize="small" />
          {img ? t("Update") : t("Add")}
        </MenuItem>
        {img ? (
          <MenuItem onClick={handleRemoveClick} sx={{ p: 2 }}>
            <Delete sx={{ mr: 1, color: CRIMSON_RED }} fontSize="small" />
            {t("Remove")}
          </MenuItem>
        ) : null}
      </Popover>
      <Dialog
        fullScreen={isMobile}
        fullWidth
        maxWidth="md"
        open={openDialog}
        onClose={handleDialogClose}
      >
        <DialogContent
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack width={"100%"}>
            <DropzoneArea
              onDropRejected={(rejectedFiles) => {
                const file = rejectedFiles[0];
                if (isHeicFile(file)) {
                  handleSnackbar("HeicNotSupported", snackbarSeverity.ERROR);
                  return;
                }
                if (file && !supportedImageTypes.includes(file.type)) {
                  handleSnackbar("UnsupportedFileType", snackbarSeverity.ERROR);
                } else if (file && file.size > maxImageUploadSize) {
                  handleSnackbar("FileTooLarge", snackbarSeverity.ERROR);
                } else {
                  handleSnackbar("SomethingWentWrong", snackbarSeverity.ERROR);
                }
              }}
              maxFileSize={maxImageUploadSize}
              onChange={(files) => handleSelectedFile(files[0])}
              dropzoneText={t("DragnDropHere")}
              acceptedFiles={supportedImageTypes}
              filesLimit={1}
              showAlerts={false}
              showPreviewsInDropzone={false}
              clearOnUnmount
            />
            <Typography variant="subtitle1">
              {t("SupportedFileTypes")}:{" "}
              {supportedImageTypes.map((val, index) => {
                var split = val.split("image/").pop();
                if (index < supportedImageTypes.length - 1) {
                  return split + ", ";
                }
                return split;
              })}
            </Typography>
            {selectedFile && (
              <Box mt={2}>
                <Chip
                  label={trimText(selectedFile.name, 20)}
                  onDelete={() => setSelectedFile(null)}
                  sx={{
                    margin: "5px",
                    minWidth: CHIP_MIN_WIDTH,
                    justifyContent: "start",
                  }}
                />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!selectedFile}
          >
            {t("Save")}
          </Button>
          <Button color="error" onClick={handleDialogClose}>
            {t("Cancel")}
          </Button>
        </DialogActions>
      </Dialog>
      <deleting.ConfirmationDialog />
    </Box>
  );
}
